import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { mapVoiceSessionToConversation } from './conversations';
import type { ConversationSession } from './conversations';
import { normalizeConversationTranscript } from './knowledgeBase';

// ── Conversation full detail (§4.4 + §12) ────────────────────────────

export type ConversationSummary = {
  outcome: string;
  summary: string;
  key_topics: string[];
  action_items: string[];
  caller_intent: string;
  sentiment: number;
  qualification_score: number | null;
};

export type ConversationContact = {
  id?: string;
  name?: string;
  phone?: string;
  email?: string;
  zip_code?: string;
};

export type ConversationAppointment = {
  id?: string;
  service_type?: string;
  preferred_date?: string;
  preferred_time?: string;
  status?: string;
  confirmation_number?: string;
};

export type ConversationSignal = {
  id: string;
  signal_type: string;
  turn_number: number;
  payload: Record<string, unknown>;
  confidence: number;
  created_at: string;
};

export type ConversationFullDetail = ConversationSession & {
  summary: ConversationSummary | null;
  channel: string;
  recipient_token: string | null;
  creative_token: string | null;
  events: unknown[];
  contacts: ConversationContact[];
  appointments: ConversationAppointment[];
  callbacks: unknown[];
  signals: ConversationSignal[];
};

/** Fetch the full conversation detail from `GET /api/analytics/conversation/{session_id}`.
 *  Flattens resp.session into the result and adds events/contacts/appointments/callbacks/signals. */
export async function fetchConversationDetail(sessionId: string, axiosInstance?: AxiosInstance): Promise<ConversationFullDetail | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.analytics}/conversation/${sessionId}`;
  const response = await client.get<unknown>(url);
  const resp = response.data as any;
  if (!resp) return null;

  const session = (resp.session && typeof resp.session === 'object') ? resp.session : resp;
  const lines = normalizeConversationTranscript(resp);
  const mapped = mapVoiceSessionToConversation(session);

  const rawSummary = session.summary;
  const summary: ConversationSummary | null =
    rawSummary && typeof rawSummary === 'object'
      ? {
          outcome: String(rawSummary.outcome ?? ''),
          summary: String(rawSummary.summary ?? ''),
          key_topics: Array.isArray(rawSummary.key_topics) ? rawSummary.key_topics.map(String) : [],
          action_items: Array.isArray(rawSummary.action_items) ? rawSummary.action_items.map(String) : [],
          caller_intent: String(rawSummary.caller_intent ?? ''),
          sentiment: Number(rawSummary.sentiment ?? mapped.sentiment),
          qualification_score: rawSummary.qualification_score != null ? Number(rawSummary.qualification_score) : null,
        }
      : null;

  return {
    ...mapped,
    transcript: lines,
    summary,
    channel: String(session.channel ?? ''),
    recipient_token: session.recipient_token ?? null,
    creative_token: session.creative_token ?? null,
    events: Array.isArray(resp.events) ? resp.events : [],
    contacts: Array.isArray(resp.contacts) ? resp.contacts : [],
    appointments: Array.isArray(resp.appointments) ? resp.appointments : [],
    callbacks: Array.isArray(resp.callbacks) ? resp.callbacks : [],
    signals: Array.isArray(resp.signals) ? resp.signals : [],
  };
}
